import React, { useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Badge, IconButton, Menu, MenuItem, ListItemText, Typography } from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';

import { powertrainAtom } from 'atoms/powertrainAtom';
import { copyToConfigAtom } from 'atoms/Rtool/copyToConfigAtoms';
import { useActiveCopyJobEntries } from 'utils/hooks/useCopyJobBlocking';
import { buildCopyJobDestinationLabel, buildCopyJobProgressCaption } from 'utils/copyJobProgress';
import type { CopyJobEntry } from 'atoms/Rtool/copyJobAtoms';

// 実行中コピーの常設インジケーター（issue #44 §2.3、任意機能）。
// コピー先画面を離れても進行が分かるよう、件数バッジ付きボタン→クリックで一覧表示する。
// 該当画面を一度も開いていない（copyJobsAtomにエントリが無い）間は何も表示しない
// （issue #41 §2.3: 実行中ジョブを横断で探すAPIが無いため、これは仕様どおりの挙動）。
export const CopyJobIndicator: React.FC = () => {
    const activeEntries = useActiveCopyJobEntries();
    const setPowertrain = useSetAtom(powertrainAtom);
    const setCopyToConfig = useSetAtom(copyToConfigAtom);
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    // 最後のジョブが終端に達してコンポーネントがnullを返すようになった後、次のジョブが始まった
    // 際にopen状態のanchorElが検出済みノードを指し続けないようにする（/code-review所見）。
    useEffect(() => {
        if (activeEntries.length === 0) setAnchorEl(null);
    }, [activeEntries.length]);

    if (activeEntries.length === 0) return null;

    // watchState==='error'（監視を諦めた状態）は「実行中」ではないため区別する
    // （/code-review所見: errorしか無くても「実行中」と表示され続けると停止に気づけない）。
    const runningCount = activeEntries.filter((entry) => entry.watchState === 'watching').length;
    const hasError = activeEntries.some((entry) => entry.watchState === 'error');
    const label = runningCount > 0
        ? `実行中のコピー ${activeEntries.length}件`
        : `確認が必要なコピー ${activeEntries.length}件`;

    // /code-review所見: useReselectCalc（rStageConfigAtom→/existing-project）は既存計算セット
    // 選択画面向けで、DataRegistration画面のコピー先選択（copyToConfigAtom）とは独立した別の状態。
    // そのため誤って遷移先にコピー先が復元されず進捗が出ない不具合になっていた。
    // ここではコピー先screenが実際に参照するcopyToConfigAtomを直接更新して遷移する。
    const goToDestination = (entry: CopyJobEntry) => {
        setAnchorEl(null);
        setPowertrain(entry.powertrain);
        setCopyToConfig(entry.config);
        navigate('/r-tools/data-registration');
    };

    return (
        <>
            <IconButton
                color="inherit"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                aria-label={label}
                title={label}
            >
                <Badge badgeContent={activeEntries.length} color={hasError && runningCount === 0 ? 'error' : 'secondary'}>
                    <CloudSyncIcon />
                </Badge>
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Typography variant="overline" sx={{ px: 2, color: 'text.secondary' }}>
                    実行中のコピー
                </Typography>
                {activeEntries.map((entry) => (
                    <MenuItem key={entry.destinationKey} onClick={() => goToDestination(entry)}>
                        <ListItemText
                            primary={buildCopyJobDestinationLabel(entry)}
                            secondary={buildCopyJobProgressCaption(entry)}
                        />
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};
